export interface RoleBasedItem {
  allowedRoles: number[]
}

export interface User {
  id?: number
  role_id?: number | null
  subscription_status?: string
  full_name?: string
  first_name?: string
  last_name?: string
  email?: string
  status?: number
  stripe_customer_id?: string
  stripe_subscription_id?: string
}

// Permission helpers
export const hasPermission = (permissions: string[], requiredPermission: string): boolean => {
  return permissions.includes(requiredPermission)
}

export const getInitials = (name: string): string =>
  name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()

export function filterByRole<T extends RoleBasedItem>(
  items: T[],
  roleId: number | undefined | null
): T[] {
  if (roleId == null) return []
  return items.filter((i) => i.allowedRoles.includes(roleId))
}

export function filterHeadersByUserRole<T extends RoleBasedItem>(
  headers: T[],
  currentUser: {role_id?: number | null}
): T[] {
  return filterByRole(headers, currentUser.role_id)
}

// Subscription Status Constants
export const SUBSCRIPTION_STATUSES = {
  ACTIVE: 'active',
  TRIAL: 'trial',
  CANCELLED: 'cancelled',
  TERMINATED: 'terminated',
  INACTIVE: 'inactive',
  FAILED: 'failed',
  PAST_DUE: 'past_due',
} as const

export type SubscriptionStatus = typeof SUBSCRIPTION_STATUSES[keyof typeof SUBSCRIPTION_STATUSES]

// Valid subscription statuses (user can access features)
const VALID_SUBSCRIPTION_STATUSES: SubscriptionStatus[] = [
  SUBSCRIPTION_STATUSES.ACTIVE,
  SUBSCRIPTION_STATUSES.TRIAL,
  SUBSCRIPTION_STATUSES.CANCELLED,
  SUBSCRIPTION_STATUSES.PAST_DUE, // Allow access during grace period
]

/**
 * Check if user has a valid subscription (active, trial, or cancelled)
 * @param user - User object or subscription status string
 * @returns boolean indicating if user has valid subscription
 */
export const isUserSubscribed = (user: User | string | null | undefined): boolean => {
  if (!user) return false

  const status = typeof user === 'string' ? user : user.subscription_status
  if (!status) return false

  return VALID_SUBSCRIPTION_STATUSES.includes(status as SubscriptionStatus)
}

/**
 * Check if user has active subscription
 * @param user - User object or subscription status string
 * @returns boolean indicating if user has active subscription
 */
export const isUserActive = (user: User | string | null | undefined): boolean => {
  if (!user) return false

  const status = typeof user === 'string' ? user : user.subscription_status
  return status === SUBSCRIPTION_STATUSES.ACTIVE
}

/**
 * Check if user is on trial
 * @param user - User object or subscription status string
 * @returns boolean indicating if user is on trial
 */
export const isUserOnTrial = (user: User | string | null | undefined): boolean => {
  if (!user) return false

  const status = typeof user === 'string' ? user : user.subscription_status
  return status === SUBSCRIPTION_STATUSES.TRIAL
}

/**
 * Check if user subscription is cancelled
 * @param user - User object or subscription status string
 * @returns boolean indicating if user subscription is cancelled
 */
export const isUserCancelled = (user: User | string | null | undefined): boolean => {
  if (!user) return false

  const status = typeof user === 'string' ? user : user.subscription_status
  return status === SUBSCRIPTION_STATUSES.CANCELLED
}

/**
 * Check if user subscription is inactive
 * @param user - User object or subscription status string
 * @returns boolean indicating if user subscription is inactive
 */
export const isUserInactive = (user: User | string | null | undefined): boolean => {
  if (!user) return false

  const status = typeof user === 'string' ? user : user.subscription_status
  return status === SUBSCRIPTION_STATUSES.INACTIVE
}

/**
 * Check if user subscription has failed (initial payment failure - never successfully subscribed)
 * @param user - User object or subscription status string
 * @returns boolean indicating if user subscription has failed
 */
export const isUserFailed = (user: User | string | null | undefined): boolean => {
  if (!user) return false

  const status = typeof user === 'string' ? user : user.subscription_status
  return status === SUBSCRIPTION_STATUSES.FAILED
}

/**
 * Check if user subscription is past due (renewal payment failure - had active subscription)
 * @param user - User object or subscription status string
 * @returns boolean indicating if user subscription is past due
 */
export const isUserPastDue = (user: User | string | null | undefined): boolean => {
  if (!user) return false

  const status = typeof user === 'string' ? user : user.subscription_status
  return status === SUBSCRIPTION_STATUSES.PAST_DUE
}

/**
 * Check if user subscription is terminated
 * @param user - User object or subscription status string
 * @returns boolean indicating if user subscription is terminated
 */
export const isUserTerminated = (user: User | string | null | undefined): boolean => {
  if (!user) return false

  const status = typeof user === 'string' ? user : user.subscription_status
  return status === SUBSCRIPTION_STATUSES.TERMINATED
}

/**
 * Check if user has active or cancelled subscription (for billing access)
 * @param user - User object or subscription status string
 * @returns boolean indicating if user has active or cancelled subscription
 */
export const isUserActiveOrCancelled = (user: User | string | null | undefined): boolean => {
  if (!user) return false

  const status = typeof user === 'string' ? user : user.subscription_status
  return status === SUBSCRIPTION_STATUSES.ACTIVE || status === SUBSCRIPTION_STATUSES.CANCELLED
}

/**
 * Check if user has active, cancelled, or past_due subscription (for billing access)
 * @param user - User object or subscription status string
 * @returns boolean indicating if user has billing access
 */
export const hasUserBillingAccess = (user: User | string | null | undefined): boolean => {
  if (!user) return false

  const status = typeof user === 'string' ? user : user.subscription_status
  return (
    status === SUBSCRIPTION_STATUSES.ACTIVE ||
    status === SUBSCRIPTION_STATUSES.CANCELLED ||
    status === SUBSCRIPTION_STATUSES.PAST_DUE
  )
}

/**
 * Check if user has ever had a paid subscription (excludes trial, inactive, and failed)
 * @param user - User object or subscription status string
 * @returns boolean indicating if user has ever successfully subscribed with payment
 */
export const isUserEverSubscribed = (user: User | string | null | undefined): boolean => {
  if (!user) return false

  const status = typeof user === 'string' ? user : user.subscription_status
  return (
    status === SUBSCRIPTION_STATUSES.ACTIVE ||
    status === SUBSCRIPTION_STATUSES.CANCELLED ||
    status === SUBSCRIPTION_STATUSES.TERMINATED ||
    status === SUBSCRIPTION_STATUSES.PAST_DUE // Past due means they had an active subscription that failed to renew
  )
}

/**
 * Check if user can add patients
 * @param user - User object or subscription status string
 * @returns boolean indicating if user can add patients
 */
export const canAddPatients = (user: User | string | null | undefined): boolean => {
  if (!user) return false

  // Business Rule: Anyone can REDUCE quantity (seats) at any time
  // Business Rule: INCREASE quantity is blocked during cancellation period (avoid charging users who requested cancellation)
  // Business Rule: Terminated users can add patients (they're like new users - no active subscription to manage)
  const status = typeof user === 'string' ? user : user.subscription_status
  return status !== SUBSCRIPTION_STATUSES.CANCELLED
}

/**
 * Get user subscription status display label
 * @param user - User object or subscription status string
 * @returns formatted status label
 */
export const getUserSubscriptionLabel = (user: User | string | null | undefined): string => {
  if (!user) return 'Unknown'

  const status = typeof user === 'string' ? user : user.subscription_status
  if (!status) return 'Unknown'

  // Capitalize first letter
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

/**
 * Get user display name from first_name, last_name, or full_name
 * @param user - User object
 * @returns formatted display name
 */
export const getUserDisplayName = (user: User | null | undefined): string => {
  if (!user) return 'Unknown User'

  if (user.full_name) return user.full_name
  if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`
  if (user.first_name) return user.first_name
  if (user.email) return user.email

  return 'Unknown User'
}

// ========================================
// DEPRECATED TYPES - Use RoleBasedItem instead
// ========================================

/**
 * @deprecated Use RoleBasedItem instead
 */
export interface Roleled extends RoleBasedItem {}
